/**
 * ky HTTPError 판별 — 서버가 응답한 오류인지(네트워크·파싱 오류와 구분).
 *
 * 서버의 4xx 응답은 전역 QueryCache/MutationCache가 `messageKey`를 번역해 토스트로
 * 이미 보여준다. 개별 뮤테이션이 같은 실패에 토스트를 하나 더 띄우면 오류 토스트가
 * 2개가 되므로(QA ARC-114·ARC-123), 개별 토스트는 전역이 다루지 않는 경우에만 띄운다.
 */
export const httpStatusOf = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) return undefined;
  const response = (error as { response?: unknown }).response;
  return response instanceof Response ? response.status : undefined;
};

/** 전역 오류 토스트(서버 messageKey)가 사유를 보여주는 실패인지 — 4xx */
export const isShownByGlobalToast = (error: unknown) => {
  const status = httpStatusOf(error);
  return status != null && status >= 400 && status < 500;
};

/** 서버 오류 응답 본문의 문자열 필드 하나 — 본문을 복제해 읽으므로 같은 응답을 다시 읽어도 된다 */
const errorBodyField = async (
  error: unknown,
  field: 'messageKey' | 'message' | 'detail',
): Promise<string | undefined> => {
  if (typeof error !== 'object' || error === null) return undefined;
  const response = (error as { response?: unknown }).response;
  if (!(response instanceof Response)) return undefined;
  try {
    const body: unknown = await response.clone().json();
    const value = (body as Record<string, unknown> | null)?.[field];
    return typeof value === 'string' && value ? value : undefined;
  } catch {
    return undefined;
  }
};

/** 서버 오류 응답의 `messageKey`(i18n `indrive.errors` 키). 없거나 못 읽으면 undefined. */
export const messageKeyOf = (error: unknown) =>
  errorBodyField(error, 'messageKey');

/** 서버 오류 응답의 `message`(서버가 만든 문구). 없거나 못 읽으면 undefined. */
export const serverMessageOf = (error: unknown) =>
  errorBodyField(error, 'message');

/**
 * 서버 오류 응답의 `detail`(ProblemDetails — 그 요청에 맞춘 구체적인 사유).
 * 예: 선택지 삭제 409 "1개 파일이 사용 중인 선택지는 삭제할 수 없습니다. …"(SET-16).
 */
export const serverDetailOf = (error: unknown) =>
  errorBodyField(error, 'detail');

/**
 * 요청 실패를 던지지 않고 값으로 돌려준다(성공이면 `data`). 뮤테이션 안에서 쓰면 전역
 * MutationCache가 오류 토스트를 띄우지 않으므로, 호출부가 여러 결과를 모아 사유까지
 * 토스트 1개로 안내할 수 있다(ARC-122 · ARC-85 — 전역 오류 + 결과 안내로 알림 2개가 뜨던 문제).
 */
export const settle = async <T>(
  request: () => Promise<T>,
): Promise<{ data?: T; error?: unknown }> => {
  try {
    return { data: await request() };
  } catch (error) {
    return { error };
  }
};

/**
 * 예상된 실패 상태(예: 403·404)를 null로 바꾼다 — 던지면 전역 QueryCache가 요청마다
 * 오류 토스트를 띄운다. 화면은 null을 보고 안내로 대신하거나 조용히 넘어간다.
 */
export const nullOnStatus = async <T>(
  request: () => Promise<T>,
  statuses: number[],
): Promise<T | null> => {
  try {
    return await request();
  } catch (error) {
    const status = httpStatusOf(error);
    if (status != null && statuses.includes(status)) return null;
    throw error;
  }
};
